import { useContext, useState, useRef, useEffect } from 'react';
import { __ } from '@wordpress/i18n';
import SbUtils from '../../Utils/SbUtils';
import { hexToHue, hueToHex } from '../../Utils/ColorUtils';
import ReviewAlertEditorContext from '../Context/ReviewAlertEditorContext';
import ThemeSelector from './ThemeSelector';
import VisibilitySelector from './VisibilitySelector';
import FeedSources from '../Common/FeedSources';
import PopupSectionHeader from './PopupSectionHeader';
import { default as ReactSliderInput } from 'react-input-slider';

// Section hierarchy for determining navigation direction
const SECTION_DEPTH = {
    'sb-popup-tab': 0,
    'sb-popup-settings-tab': 0,
    'sb-popup-content-tab': 1,
    'sb-popup-review-feed-section': 1,
    'sb-popup-sources-tab': 1,
    'sb-popup-filter-tab': 1,
    'sb-popup-visibility-tab': 1,
    'sb-popup-theme-section': 2
};

// Theme options for the popup (all map to light or dark theme)
const THEME_OPTIONS = [
    { value: 'light', label: 'Light', description: 'Light theme with blue accents' },
    { value: 'dark', label: 'Dark', description: 'Dark blue theme', isPro: true },
    { value: 'minimal', label: 'Minimal', description: 'Minimal light theme', isPro: true },
    { value: 'minimal-dark', label: 'Minimal Dark', description: 'Minimal dark theme', isPro: true }
];

// Predefined accent colors matching prototype (hue-based, displayed using OKLCH)
const PRESET_COLORS = [
    { name: 'Red', hue: 15 },
    { name: 'Orange', hue: 40 },
    { name: 'Yellow', hue: 70 },
    { name: 'Green', hue: 145 },
    { name: 'Teal', hue: 180 },
    { name: 'Blue', hue: 250 },
    { name: 'Purple', hue: 280 },
    { name: 'Pink', hue: 330 }
];

// Position options for the popup
const POSITION_OPTIONS = [
    { value: 'bottom-left', label: 'Left' },
    { value: 'bottom-right', label: 'Right' }
];

// Button icon options for the Review Feed header button
const BUTTON_ICONS = [
    { id: 'arrow-right', label: 'Arrow Right', svg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor"><path fill-rule="evenodd" d="M2 8a.75.75 0 0 1 .75-.75h8.69L8.22 4.03a.75.75 0 0 1 1.06-1.06l4.5 4.5a.75.75 0 0 1 0 1.06l-4.5 4.5a.75.75 0 0 1-1.06-1.06l3.22-3.22H2.75A.75.75 0 0 1 2 8Z" clip-rule="evenodd" /></svg>' },
    { id: 'external-link', label: 'External Link', svg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor"><path d="M6.22 8.72a.75.75 0 0 0 1.06 1.06l5.22-5.22v1.69a.75.75 0 0 0 1.5 0v-3.5a.75.75 0 0 0-.75-.75h-3.5a.75.75 0 0 0 0 1.5h1.69L6.22 8.72Z" /><path d="M3.5 6.75c0-.69.56-1.25 1.25-1.25H7A.75.75 0 0 0 7 4H4.75A2.75 2.75 0 0 0 2 6.75v4.5A2.75 2.75 0 0 0 4.75 14h4.5A2.75 2.75 0 0 0 12 11.25V9a.75.75 0 0 0-1.5 0v2.25c0 .69-.56 1.25-1.25 1.25h-4.5c-.69 0-1.25-.56-1.25-1.25v-4.5Z" /></svg>' },
    { id: 'chevron-right', label: 'Chevron Right', svg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor"><path fill-rule="evenodd" d="M6.22 4.22a.75.75 0 0 1 1.06 0l3.25 3.25a.75.75 0 0 1 0 1.06l-3.25 3.25a.75.75 0 0 1-1.06-1.06L8.94 8 6.22 5.28a.75.75 0 0 1 0-1.06Z" clip-rule="evenodd" /></svg>' },
    { id: 'star', label: 'Star', svg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor"><path fill-rule="evenodd" d="M8 1.75a.75.75 0 0 1 .692.462l1.41 3.393 3.664.293a.75.75 0 0 1 .428 1.317l-2.791 2.39.853 3.575a.75.75 0 0 1-1.12.814L7.998 12.08l-3.135 1.915a.75.75 0 0 1-1.12-.814l.852-3.574-2.79-2.39a.75.75 0 0 1 .427-1.318l3.663-.293 1.41-3.393A.75.75 0 0 1 8 1.75Z" clip-rule="evenodd" /></svg>' },
    { id: 'heart', label: 'Heart', svg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor"><path d="M2 6.342a3.375 3.375 0 0 1 6-2.088 3.375 3.375 0 0 1 5.997 2.26c-.063 2.134-1.618 3.76-2.955 4.784a14.437 14.437 0 0 1-2.676 1.61c-.02.01-.038.017-.053.022l-.018.007-.004.002h-.002a.75.75 0 0 1-.592 0h-.001l-.005-.002-.018-.007a5.386 5.386 0 0 1-.053-.022 14.437 14.437 0 0 1-2.676-1.61C3.618 10.102 2.063 8.476 2 6.342Z" /></svg>' }
];

// Show/Hide elements configuration - moved outside component to prevent recreation on every render
// Aggregate view: fewer options (rating, total reviews, powered by)
const AGGREGATE_ELEMENTS = [
    { key: 'show_rating', label: 'Rating' },
    { key: 'show_total_reviews', label: 'Total Reviews' },
    { key: 'show_powered_by', label: 'Powered By', isPro: true }
];

// Recent reviews view: all options for individual reviews (matches Prototype)
const RECENT_ELEMENTS = [
    { key: 'show_avatar', label: 'Avatar' },
    { key: 'show_platform', label: 'Platform Badge' },
    { key: 'show_rating', label: 'Rating' },
    { key: 'show_reviewer_name', label: 'Author Name' },
    { key: 'show_date', label: 'Date' },
    { key: 'show_review_text', label: 'Review Text' },
    { key: 'show_powered_by', label: 'Powered By', isPro: true }
];

// Review card elements for expanded view
const REVIEW_CARD_ELEMENTS = [
    { key: 'show_stars', label: 'Stars' },
    { key: 'show_title', label: 'Title' },
    { key: 'show_content', label: 'Content' },
    { key: 'show_author', label: 'Author' },
    { key: 'show_date', label: 'Date' }
];

/**
 * Get normalized theme value (handles backwards compatibility)
 */
const getNormalizedTheme = ( theme ) => {
    // Map old 'default' value to 'light'
    if ( !theme || theme === 'default' ) return 'light';
    return theme;
};

/**
 * Review Alert Sidebar Component
 *
 * Provides the customizer sidebar for editing review alert settings.
 * Sections per documentation:
 * 1. Review Alert (main) - Theme, Variation, Accent Color, Position
 * 2. Content - Show/hide toggles
 * 3. Behavior - Timing controls
 * 4. Location - Page targeting
 */
const ReviewAlertSidebar = () => {
    const {
        sbCustomizer,
        popupData,
        updatePopupSetting,
        updateNestedPopupSetting,
        sidebarSection,
        isPro,
        upsellModal,
        sources,
        fbModal,
        fbManualModal,
        previewReviews,
        totalReviews,
        unfilteredTotal,
        setIsPreviewExpanded
    } = useContext( ReviewAlertEditorContext );

    const settings = popupData?.settings || {};

    // Track expanded state for collapsible sections
    const [ expandedSections, setExpandedSections ] = useState( {} );

    // Custom color hex input
    const [ customHexInput, setCustomHexInput ] = useState( settings?.accent_color || '#175CE3' );

    // Hue slider value (0-360)
    const [ hueValue, setHueValue ] = useState( () => {
        // Use saved hue if available, otherwise convert from hex
        if ( settings?.accent_hue ) {
            return parseInt( settings.accent_hue, 10 );
        }
        const hex = settings?.accent_color || '#175CE3';
        return hexToHue( hex );
    } );

    // Track if this is the initial mount to avoid syncing on user changes
    const isInitialMount = useRef( true );
    const lastSyncedAccentColor = useRef( settings?.accent_color );
    const lastSyncedAccentHue = useRef( settings?.accent_hue );

    // Sync local accent color states when popupData changes from backend
    // This handles the case where the page reloads and initial state was set before data loaded
    useEffect( () => {
        // Skip on initial mount - useState already handles that
        if ( isInitialMount.current ) {
            isInitialMount.current = false;
            return;
        }

        // Only sync if the settings values changed from what we last synced
        // This prevents overwriting user changes with stale values
        const newAccentColor = settings?.accent_color;
        const newAccentHue = settings?.accent_hue;

        // Check if accent_color or accent_hue changed from backend (not from user input)
        const colorChangedFromBackend = newAccentColor && newAccentColor !== lastSyncedAccentColor.current;
        const hueChangedFromBackend = newAccentHue && newAccentHue !== lastSyncedAccentHue.current;

        if ( colorChangedFromBackend || hueChangedFromBackend ) {
            // Update the local states to match the new backend values
            if ( newAccentColor ) {
                setCustomHexInput( newAccentColor );
                lastSyncedAccentColor.current = newAccentColor;
            }

            if ( newAccentHue ) {
                setHueValue( parseInt( newAccentHue, 10 ) );
                lastSyncedAccentHue.current = newAccentHue;
            } else if ( newAccentColor ) {
                // If only color is set, derive hue from it
                const derivedHue = hexToHue( newAccentColor );
                setHueValue( derivedHue );
            }
        }
    }, [ settings?.accent_color, settings?.accent_hue ] );

    // Slide animation direction: 'forward' (slide from right) or 'back' (slide from left)
    const [ slideDirection, setSlideDirection ] = useState( null );
    const [ , setIsAnimating ] = useState( false );
    const prevSectionRef = useRef( sidebarSection.currentSidebarSection );

    /**
     * Navigate to a section with slide animation
     */
    const navigateToSection = ( targetSection ) => {
        const currentSection = sidebarSection.currentSidebarSection;
        const currentDepth = SECTION_DEPTH[ currentSection ] ?? 0;
        const targetDepth = SECTION_DEPTH[ targetSection ] ?? 0;

        // Determine direction: going deeper = forward, going back = back
        const direction = targetDepth > currentDepth ? 'forward' : 'back';

        setSlideDirection( direction );
        setIsAnimating( true );

        // Small delay to allow animation class to be applied
        setTimeout( () => {
            sidebarSection.setCurrentSidebarSection( targetSection );
        }, 10 );
    };

    // Reset animation state after section change
    useEffect( () => {
        if ( prevSectionRef.current !== sidebarSection.currentSidebarSection ) {
            prevSectionRef.current = sidebarSection.currentSidebarSection;
            // Allow animation to complete, then reset
            const timer = setTimeout( () => {
                setIsAnimating( false );
                setSlideDirection( null );
            }, 250 );
            return () => clearTimeout( timer );
        }
    }, [ sidebarSection.currentSidebarSection ] );

    /**
     * Toggle section expansion (accordion behavior - only one open at a time)
     */
    const toggleSection = ( sectionId ) => {
        setExpandedSections( prev => {
            // If clicking on already open section, close it
            if ( prev[ sectionId ] ) {
                return { ...prev, [ sectionId ]: false };
            }
            // Otherwise, close all and open the clicked one
            return { [ sectionId ]: true };
        } );
    };

    /**
     * Get the current theme label for display
     */
    const getCurrentThemeLabel = () => {
        const currentTheme = getNormalizedTheme( settings?.theme );
        const themeOption = THEME_OPTIONS.find( opt => opt.value === currentTheme );
        return themeOption ? themeOption.label : 'Light';
    };

    /**
     * Check if a theme option is currently selected
     */
    const isThemeSelected = ( optionValue ) => {
        const currentTheme = getNormalizedTheme( settings?.theme );
        return currentTheme === optionValue;
    };

    /**
     * Get the current position label for display
     */
    const getCurrentPositionLabel = () => {
        const currentPosition = settings?.position || 'bottom-right';
        const positionOption = POSITION_OPTIONS.find( opt => opt.value === currentPosition );
        return positionOption ? positionOption.label : 'Right';
    };

    /**
     * Check if a position option is currently selected
     */
    const isPositionSelected = ( optionValue ) => {
        const currentPosition = settings?.position || 'bottom-right';
        return currentPosition === optionValue;
    };

    /**
     * Handle theme change from ThemeSelector
     */
    const handleThemeChange = ( { theme } ) => {
        updatePopupSetting( 'theme', theme );
    };

    /**
     * Handle Pro-gated setting click
     */
    // eslint-disable-next-line no-unused-vars
    const handleProSettingClick = ( settingKey, modalKey ) => {
        if ( !isPro ) {
            SbUtils.openUpsellModal( modalKey, upsellModal );
            return false;
        }
        return true;
    };

    /**
     * Handle theme selection
     */
    const handleThemeSelect = ( themeValue ) => {
        const themeOption = THEME_OPTIONS.find( opt => opt.value === themeValue );

        // Check if Pro theme selected without Pro license
        if ( themeOption?.isPro && !isPro ) {
            // Convert hyphenated theme name to PascalCase (e.g., 'minimal-dark' → 'MinimalDark')
            const pascalCaseTheme = themeValue
                .split( '-' )
                .map( part => part.charAt( 0 ).toUpperCase() + part.slice( 1 ) )
                .join( '' );
            SbUtils.openUpsellModal( `reviewAlert${pascalCaseTheme}Theme`, upsellModal );
            return;
        }

        updatePopupSetting( 'theme', themeValue );
    };

    /**
     * Handle accent color selection
     */
    // eslint-disable-next-line no-unused-vars
    const handleAccentColorSelect = ( color ) => {
        updatePopupSetting( 'accent_color', color );
        setCustomHexInput( color );
        // Convert hex to hue for HSL theming
        const hue = hexToHue( color );
        updatePopupSetting( 'accent_hue', String( hue ) );
        setHueValue( hue );
        // Update refs to prevent sync useEffect from overwriting user changes
        lastSyncedAccentColor.current = color;
        lastSyncedAccentHue.current = String( hue );
    };

    /**
     * Handle custom hex input change
     */
    const handleCustomHexChange = ( e ) => {
        let value = e.target.value.toUpperCase();
        // Add # if not present
        if ( value && !value.startsWith( '#' ) ) {
            value = '#' + value;
        }
        setCustomHexInput( value );
        // Only update if valid hex
        if ( /^#[0-9A-F]{6}$/i.test( value ) ) {
            updatePopupSetting( 'accent_color', value );
            // Convert hex to hue for HSL theming
            const hue = hexToHue( value );
            updatePopupSetting( 'accent_hue', String( hue ) );
            setHueValue( hue );
            // Update refs to prevent sync useEffect from overwriting user changes
            lastSyncedAccentColor.current = value;
            lastSyncedAccentHue.current = String( hue );
        }
    };

    /**
     * Check if a hue preset is currently selected (with tolerance)
     */
    const isHueSelected = ( presetHue ) => {
        return Math.abs( hueValue - presetHue ) < 10;
    };

    /**
     * Handle preset hue selection
     */
    const handlePresetHueSelect = ( presetHue ) => {
        setHueValue( presetHue );
        const hexColor = hueToHex( presetHue );
        setCustomHexInput( hexColor );
        updatePopupSetting( 'accent_color', hexColor );
        updatePopupSetting( 'accent_hue', String( presetHue ) );
        // Update refs to prevent sync useEffect from overwriting user changes
        lastSyncedAccentColor.current = hexColor;
        lastSyncedAccentHue.current = String( presetHue );
    };

    /**
     * Render main popup section (Customize tab)
     */
    const renderMainSection = () => (
        <>
            {/* Theme Section - Dropdown Menu */}
            <div className="sb-customizer-sidebar-sec-el-dropdown sb-fs">
                <div
                    className={ `sb-customizer-sidebar-sec-el sb-fs ${ expandedSections.theme ? 'sb-dropdown-expanded' : '' }` }
                    onClick={ () => toggleSection( 'theme' ) }
                >
                    { SbUtils.printIcon( 'theme', 'sb-customizer-sidebar-sec-el-icon' ) }
                    <span className="sb-small-p sb-bold sb-dark-text">
                        { __( 'Theme', 'reviews-feed' ) }
                    </span>
                    <span className="sb-customizer-sec-el-value">
                        { getCurrentThemeLabel() }
                        { SbUtils.printIcon( 'chevron-bottom', 'sb-customizer-sec-el-chevron' ) }
                    </span>
                </div>

                {/* Absolute Positioned Dropdown */}
                <div className={ `sb-theme-dropdown sb-toggleset-ctn sb-dropdown-animate ${ expandedSections.theme ? 'sb-dropdown-open' : '' }` }>
                    { THEME_OPTIONS.map( ( option ) => {
                        const isSettingPro = option.isPro && !isPro;
                        return (
                            <div
                                key={ option.value }
                                className="sb-toggleset-elem sb-fs"
                                data-active={ isThemeSelected( option.value ) }
                                data-disabled={ isSettingPro }
                                onClick={ () => handleThemeSelect( option.value ) }
                            >
                                <div className="sb-toggleset-elem-deco sb-tr-2"></div>
                                <div className="sb-toggleset-elem-label">
                                    <span className="sb-fs">
                                        { option.label }
                                        { isSettingPro && SbUtils.printIcon( 'rocket-premium', 'pro-rocket-icon' ) }
                                    </span>
                                </div>
                            </div>
                        );
                    } ) }
                </div>
            </div>

            {/* Accent Color Section - Dropdown Menu */}
            <div className="sb-customizer-sidebar-sec-el-dropdown sb-fs">
                <div
                    className={ `sb-customizer-sidebar-sec-el sb-fs ${ expandedSections.accentColor ? 'sb-dropdown-expanded' : '' }` }
                    onClick={ () => toggleSection( 'accentColor' ) }
                >
                    { SbUtils.printIcon( 'accentcolor', 'sb-customizer-sidebar-sec-el-icon' ) }
                    <span className="sb-small-p sb-bold sb-dark-text">
                        { __( 'Accent Color', 'reviews-feed' ) }
                    </span>
                    <span className="sb-customizer-sec-el-value">
                        <span
                            className="sb-customizer-sec-el-color-preview"
                            style={ { backgroundColor: `oklch(0.6 0.25 ${ hueValue })` } }
                        ></span>
                        { SbUtils.printIcon( 'chevron-bottom', 'sb-customizer-sec-el-chevron' ) }
                    </span>
                </div>

                {/* Expandable Color Picker */}
                <div className={ `sb-accent-color-dropdown sb-dropdown-animate ${ expandedSections.accentColor ? 'sb-dropdown-open' : '' }` }>
                    {/* Preset Colors */}
                    <div className="sb-accent-color-presets">
                        <span className="sb-accent-color-label">
                            { __( 'Choose from Presets', 'reviews-feed' ) }
                        </span>
                        <div className="sb-accent-color-circles">
                            { PRESET_COLORS.map( ( preset ) => (
                                <button
                                    key={ preset.hue }
                                    type="button"
                                    className={ `sb-color-circle ${ isHueSelected( preset.hue ) ? 'sb-color-selected' : '' }` }
                                    style={ { backgroundColor: `oklch(0.6 0.25 ${ preset.hue })` } }
                                    onClick={ () => handlePresetHueSelect( preset.hue ) }
                                    aria-label={ preset.name }
                                    title={ preset.name }
                                />
                            ) ) }
                        </div>
                    </div>

                    {/* Custom Color */}
                    <div className="sb-accent-color-custom">
                        <span className="sb-accent-color-label">
                            { __( 'Add your own', 'reviews-feed' ) }
                        </span>
                        {/* Full Color Spectrum Slider - all hues in one slider */}
                        <div
                            className="sb-hue-slider-wrapper"
                            style={ { '--slider-thumb-color': `oklch(0.6 0.25 ${ hueValue })` } }
                        >
                            <div className="sb-slider-content sb-hue-slider-content sb-fs">
                                <ReactSliderInput
                                    axis="x"
                                    xmin={ 0 }
                                    xmax={ 360 }
                                    x={ hueValue }
                                    onChange={ ( { x } ) => {
                                        setHueValue( x );
                                        const hexColor = hueToHex( x );
                                        setCustomHexInput( hexColor );
                                        // Live update during drag for real-time preview
                                        updatePopupSetting( 'accent_color', hexColor );
                                        updatePopupSetting( 'accent_hue', String( x ) );
                                        // Update refs to prevent sync useEffect from overwriting user changes
                                        lastSyncedAccentColor.current = hexColor;
                                        lastSyncedAccentHue.current = String( x );
                                    } }
                                />
                            </div>
                        </div>
                        {/* HEX Input Row */}
                        <div className="sb-accent-color-hex-row">
                            <span className="sb-accent-color-hex-label">HEX</span>
                            <input
                                type="text"
                                className="sb-accent-color-hex-input"
                                value={ customHexInput.replace( '#', '' ) }
                                onChange={ handleCustomHexChange }
                                maxLength={ 6 }
                            />
                            <span
                                className="sb-accent-color-preview-circle"
                                style={ { backgroundColor: `oklch(0.6 0.25 ${ hueValue })` } }
                            ></span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Positioning Section - Dropdown Menu */}
            <div className="sb-customizer-sidebar-sec-el-dropdown sb-fs">
                <div
                    className={ `sb-customizer-sidebar-sec-el sb-fs ${ expandedSections.positioning ? 'sb-dropdown-expanded' : '' }` }
                    onClick={ () => toggleSection( 'positioning' ) }
                >
                    { SbUtils.printIcon( 'positioning', 'sb-customizer-sidebar-sec-el-icon' ) }
                    <span className="sb-small-p sb-bold sb-dark-text">
                        { __( 'Positioning', 'reviews-feed' ) }
                    </span>
                    <span className="sb-customizer-sec-el-value">
                        { getCurrentPositionLabel() }
                        { SbUtils.printIcon( 'chevron-bottom', 'sb-customizer-sec-el-chevron' ) }
                    </span>
                </div>

                {/* Position Options Dropdown */}
                <div className={ `sb-theme-dropdown sb-toggleset-ctn sb-dropdown-animate ${ expandedSections.positioning ? 'sb-dropdown-open' : '' }` }>
                    { POSITION_OPTIONS.map( ( option ) => (
                        <div
                            key={ option.value }
                            className="sb-toggleset-elem sb-fs"
                            data-active={ isPositionSelected( option.value ) }
                            onClick={ () => updatePopupSetting( 'position', option.value ) }
                        >
                            <div className="sb-toggleset-elem-deco sb-tr-2"></div>
                            <div className="sb-toggleset-elem-label">
                                <span className="sb-fs">{ option.label }</span>
                            </div>
                        </div>
                    ) ) }
                </div>
            </div>

            {/* Separator */}
            <div className="sb-control-separator sb-control-separator--flush sb-fs"></div>

            {/* Review Alert Section */}
            <div
                className="sb-customizer-sidebar-sec-el sb-customizer-sidebar-sec-el-nav sb-fs"
                onClick={ () => { setIsPreviewExpanded( false ); navigateToSection( 'sb-popup-content-tab' ); } }
            >
                { SbUtils.printIcon( 'notificationpopup', 'sb-customizer-sidebar-sec-el-icon' ) }
                <span className="sb-small-p sb-bold sb-dark-text">
                    { __( 'Review Alert', 'reviews-feed' ) }
                </span>
                { SbUtils.printIcon( 'chevron-right', 'sb-customizer-sec-el-chevron' ) }
            </div>

            {/* Review Feed Section */}
            <div
                className="sb-customizer-sidebar-sec-el sb-customizer-sidebar-sec-el-nav sb-fs"
                style={{ marginBottom: '20px' }}
                onClick={ () => { setIsPreviewExpanded( true ); navigateToSection( 'sb-popup-review-feed-section' ); } }
            >
                { SbUtils.printIcon( 'reviewfeed', 'sb-customizer-sidebar-sec-el-icon' ) }
                <span className="sb-small-p sb-bold sb-dark-text">
                    { __( 'Review Feed', 'reviews-feed' ) }
                </span>
                { SbUtils.printIcon( 'chevron-right', 'sb-customizer-sec-el-chevron' ) }
            </div>
        </>
    );

    /**
     * Render content section (Review Alert inner screen)
     * Matches Figma design: Popup Type, Timing, Show/Hide Elements
     */
    /**
     * Render content section (Review Alert inner screen)
     * Matches Vercel demo design: Popup Type, Timing, Show/Hide Elements
     */
    const renderContentSection = () => {
        const content = settings?.content || {};
        const timing = settings?.timing || {};
        const popupType = settings?.popup_type || 'aggregate';
        const timingMode = timing?.mode || 'fixed';
        const cycleIntervalMin = Math.round( ( timing?.cycle_interval_min || 3000 ) / 1000 );
        const cycleIntervalMax = Math.round( ( timing?.cycle_interval_max || 5000 ) / 1000 );

        // Use constants defined outside component (prevents recreation on every render)
        const showHideElements = popupType === 'aggregate' ? AGGREGATE_ELEMENTS : RECENT_ELEMENTS;

        // Determine unavailable elements based on current settings
        const isAvatarHidden = content?.show_avatar === false;

        // Count visible elements, excluding unavailable ones and Powered By (branding can't be the only element)
        const visibleCount = showHideElements.filter( element => {
            const isVisible = content?.[ element.key ] !== false;
            // Powered By doesn't count - it can't be the last visible element
            if ( element.key === 'show_powered_by' ) return false;
            // Platform Badge is unavailable when Avatar is hidden
            if ( element.key === 'show_platform' && isAvatarHidden ) return false;
            return isVisible;
        } ).length;

        return (
            <div className="sb-customizer-sidebar-section-ctn sb-popup-section sb-fs">
                <PopupSectionHeader
                    title={ __( 'Review Alert', 'reviews-feed' ) }
                    onBack={ () => navigateToSection( 'sb-popup-tab' ) }
                />

                <div className="sb-popup-section-controls sb-fs">
                    {/* Popup Type */}
                    <div className="sb-popup-control-group sb-fs">
                        <div className="sb-group-heading sb-text-small sb-bold sb-fs">{ __( 'Display Type', 'reviews-feed' ) }</div>
                        <div className="sb-popup-type-options">
                            <label
                                className={ `sb-popup-type-card ${ popupType === 'aggregate' ? 'sb-active' : '' }` }
                                onClick={ () => updatePopupSetting( 'popup_type', 'aggregate' ) }
                            >
                                <span className="sb-popup-type-radio"></span>
                                <span className="sb-popup-type-content">
                                    <span className="sb-popup-type-title">{ __( 'Aggregate Review', 'reviews-feed' ) }</span>
                                    <span className="sb-popup-type-desc">{ __( 'Shows overall rating summary', 'reviews-feed' ) }</span>
                                </span>
                            </label>
                            <label
                                className={ `sb-popup-type-card ${ popupType === 'recent' ? 'sb-active' : '' }` }
                                onClick={ () => {
                                    if ( !isPro ) {
                                        SbUtils.openUpsellModal( 'reviewAlertRecentReviews', upsellModal );
                                        return;
                                    }
                                    updatePopupSetting( 'popup_type', 'recent' );
                                } }
                            >
                                <span className="sb-popup-type-radio"></span>
                                <span className="sb-popup-type-content">
                                    <span className="sb-popup-type-title">
                                        { __( 'Recent Reviews', 'reviews-feed' ) }
                                        { !isPro && SbUtils.printIcon( 'rocket-premium', 'sb-popup-pro-icon' ) }
                                    </span>
                                    <span className="sb-popup-type-desc">{ __( 'Cycles through individual reviews', 'reviews-feed' ) }</span>
                                </span>
                            </label>
                        </div>
                    </div>

                    {/* Timing - Controls review cycling interval (popup always appears after 3s) */}
                    <div className="sb-popup-control-group sb-fs">
                        <div className="sb-group-heading sb-text-small sb-bold sb-fs">{ __( 'Timing', 'reviews-feed' ) }</div>
                        <div className="sb-popup-timing-toggle">
                            <button
                                className={ `sb-popup-timing-btn ${ timingMode === 'fixed' ? 'sb-active' : '' }` }
                                onClick={ () => {
                                    // When switching from random to fixed:
                                    // Use the min value as the fixed interval (more intuitive)
                                    if ( timingMode === 'random' ) {
                                        updateNestedPopupSetting( 'timing', 'cycle_interval_max', cycleIntervalMin * 1000 );
                                    }
                                    updateNestedPopupSetting( 'timing', 'mode', 'fixed' );
                                } }
                            >
                                { __( 'Fixed', 'reviews-feed' ) }
                            </button>
                            <button
                                className={ `sb-popup-timing-btn ${ timingMode === 'random' ? 'sb-active' : '' }` }
                                onClick={ () => {
                                    // When switching from fixed to random:
                                    // - First knob (min) = current fixed value (capped at 27 to allow 3s gap)
                                    // - Second knob (max) = 30s
                                    if ( timingMode === 'fixed' ) {
                                        const newMin = Math.min( cycleIntervalMax, 27 ); // Cap at 27 to ensure 3s gap
                                        updateNestedPopupSetting( 'timing', 'cycle_interval_min', newMin * 1000 );
                                        updateNestedPopupSetting( 'timing', 'cycle_interval_max', 30 * 1000 ); // Max always 30s
                                    }
                                    updateNestedPopupSetting( 'timing', 'mode', 'random' );
                                } }
                            >
                                { __( 'Random', 'reviews-feed' ) }
                            </button>
                        </div>
                        <div className="sb-popup-timing-slider">
                            <span className="sb-popup-timing-label">
                                { timingMode === 'random' ? __( 'Cycle between', 'reviews-feed' ) : __( 'Cycle every', 'reviews-feed' ) }
                            </span>
                            <div className="sb-popup-timing-range">
                                <div className="sb-popup-dual-slider">
                                    <div className="sb-popup-dual-slider-track">
                                        <div
                                            className="sb-popup-dual-slider-fill"
                                            style={ timingMode === 'random' ? {
                                                left: `${ ( cycleIntervalMin / 30 ) * 100 }%`,
                                                width: `${ ( ( cycleIntervalMax - cycleIntervalMin ) / 30 ) * 100 }%`
                                            } : {
                                                left: '0%',
                                                width: `${ ( cycleIntervalMax / 30 ) * 100 }%`
                                            } }
                                        />
                                    </div>
                                    { timingMode === 'random' && (
                                        <input
                                            type="range"
                                            className="sb-popup-range-input sb-popup-range-min"
                                            min={ 0 }
                                            max={ 30 }
                                            value={ cycleIntervalMin }
                                            onChange={ ( e ) => {
                                                let val = parseInt( e.target.value, 10 );
                                                // Clamp to max - 3 to ensure 3s gap
                                                val = Math.min( val, cycleIntervalMax - 3 );
                                                updateNestedPopupSetting( 'timing', 'cycle_interval_min', val * 1000 );
                                            } }
                                        />
                                    ) }
                                    <input
                                        type="range"
                                        className={ `sb-popup-range-input ${ timingMode === 'random' ? 'sb-popup-range-max' : '' }` }
                                        min={ 0 }
                                        max={ 30 }
                                        value={ cycleIntervalMax }
                                        onChange={ ( e ) => {
                                            let val = parseInt( e.target.value, 10 );
                                            if ( timingMode === 'random' ) {
                                                // Clamp value to minimum of cycleIntervalMin + 3
                                                val = Math.max( val, cycleIntervalMin + 3 );
                                            }
                                            updateNestedPopupSetting( 'timing', 'cycle_interval_max', val * 1000 );
                                        } }
                                    />
                                </div>
                                <span className="sb-popup-timing-value">
                                    { timingMode === 'random' ? `${ cycleIntervalMin }s - ${ cycleIntervalMax }s` : `${ cycleIntervalMax }s` }
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Show/Hide Elements */}
                    <div className="sb-popup-control-group sb-fs" style={{ marginBottom: '20px' }}>
                        <div className="sb-group-heading sb-text-small sb-bold sb-fs sb-popup-control-heading-icon">
                            { SbUtils.printIcon( 'eye' ) }
                            { __( 'Show/Hide Elements', 'reviews-feed' ) }
                        </div>
                        <div className="sb-popup-elements-list">
                            { showHideElements.map( ( element ) => {
                                const isVisible = content?.[ element.key ] !== false;
                                const isProElement = element.isPro && !sbCustomizer?.isProPlus;
                                // Platform Badge depends on Avatar being visible
                                const isPlatformWithoutAvatar = element.key === 'show_platform' && isAvatarHidden;
                                // Combined unavailable state
                                const isUnavailable = isPlatformWithoutAvatar;
                                // Disable if this is the last visible element (must keep at least 1 visible)
                                // Powered By can't be the last visible - it's excluded from count
                                const isPoweredBy = element.key === 'show_powered_by';
                                const isLastVisible = isVisible && !isUnavailable && !isPoweredBy && visibleCount === 1;
                                const isDisabled = isLastVisible || isUnavailable;

                                // Determine tooltip text
                                let tooltipText = '';
                                if ( isPlatformWithoutAvatar ) {
                                    tooltipText = __( 'Enable Avatar to show Platform Badge', 'reviews-feed' );
                                } else if ( isLastVisible ) {
                                    tooltipText = __( 'At least one element must be visible', 'reviews-feed' );
                                }

                                return (
                                    <button
                                        key={ element.key }
                                        className={ `sb-popup-element-item ${ isVisible && !isUnavailable ? 'sb-visible' : 'sb-hidden' } ${ isDisabled ? 'sb-disabled' : '' }` }
                                        disabled={ isDisabled }
                                        title={ tooltipText }
                                        onClick={ () => {
                                            if ( isDisabled ) {
                                                return;
                                            }
                                            if ( isProElement ) {
                                                SbUtils.openUpsellModal( 'reviewAlertBranding', upsellModal );
                                                return;
                                            }
                                            updateNestedPopupSetting( 'content', element.key, !isVisible );
                                        } }
                                    >
                                        { SbUtils.printIcon( isVisible && !isUnavailable ? 'eye' : 'eyeslash', 'sb-popup-element-icon' ) }
                                        <span className="sb-popup-element-label">
                                            { __( element.label, 'reviews-feed' ) }
                                            { isProElement && SbUtils.printIcon( 'rocket-premium', 'sb-popup-pro-icon' ) }
                                        </span>
                                    </button>
                                );
                            } ) }
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    /**
     * Render behavior section
     */
    /**
     * Render filters section
     * Filters for rating, words, sources, and date
     * Uses Feed-aligned settings structure (includedStarFilters, includeWords, etc.)
     */
    const renderFiltersSection = () => {
        const filters = settings?.filters || {};
        const sort = settings?.sort || {};

        // Get popup's selected source IDs (from Settings > Sources)
        // Uses database ID instead of account_id to avoid URL encoding issues (PR #418)
        // Backward compatibility: also check account_id for existing popups with old format
        const popupSourceIds = settings?.sources || [];

        // Filter global sources list to only include popup-configured sources
        // Check both id (new format) and account_id (old format) for backward compatibility
        // Convert to strings for comparison to handle type mismatches (number vs string)
        const popupSourceIdsStr = popupSourceIds.map( s => String( s ) );
        const popupSourceObjects = ( sources?.sourcesList || [] ).filter(
            source => popupSourceIdsStr.includes( String( source.id ) ) || popupSourceIdsStr.includes( String( source.account_id ) )
        );

        // Get unique providers from popup sources only (not all global sources)
        const availableProviders = [ ...new Set(
            popupSourceObjects.map( source => source.provider ).filter( Boolean )
        ) ];

        // Selected providers: use saved filter if set, otherwise default to all available
        // Also ensure selected providers only include available ones (in case sources changed)
        const savedProviders = filters?.providers;
        const selectedProviders = savedProviders
            ? savedProviders.filter( p => availableProviders.includes( p ) )
            : availableProviders;

        // Convert includedStarFilters array to min rating for UI display
        // e.g., [3,4,5] → minRating = 3
        const includedStars = filters?.includedStarFilters || [];
        const minRating = includedStars.length > 0 ? Math.min( ...includedStars ) : 1;

        // Check if min characters filter is active (50+ characters)
        const hasMinCharFilter = filters?.filterCharCountMin > 0;

        // Get sort settings for date filter display
        const sortByDate = sort?.sortByDate || 'latest';
        const sortByDateEnabled = sort?.sortByDateEnabled !== false;

        /**
         * Update star rating filter
         * Converts min rating (1-5) to includedStarFilters array
         * e.g., minRating 3 → [3, 4, 5]
         */
        const handleRatingChange = ( newMinRating ) => {
            const newStarFilters = newMinRating === 1
                ? [] // No filter = show all
                : Array.from( { length: 6 - newMinRating }, ( _, i ) => newMinRating + i );
            updateNestedPopupSetting( 'filters', 'includedStarFilters', newStarFilters );
        };

        /**
         * Toggle min character count filter (50+ characters)
         */
        const handleMinCharToggle = ( enabled ) => {
            updateNestedPopupSetting( 'filters', 'filterCharCountMin', enabled ? 50 : 0 );
        };

        const handleProviderToggle = ( provider ) => {
            const isSelected = selectedProviders.includes( provider );

            // Prevent deselecting the last provider - must have at least one
            if ( isSelected && selectedProviders.length === 1 ) {
                return;
            }

            const newProviders = isSelected
                ? selectedProviders.filter( p => p !== provider )
                : [ ...selectedProviders, provider ];
            updateNestedPopupSetting( 'filters', 'providers', newProviders );
        };

        /**
         * Update sort by date setting
         */
        const handleSortChange = ( sortValue ) => {
            if ( sortValue === 'all' ) {
                updateNestedPopupSetting( 'sort', 'sortByDateEnabled', false );
            } else {
                updateNestedPopupSetting( 'sort', 'sortByDateEnabled', true );
                updateNestedPopupSetting( 'sort', 'sortByDate', sortValue === 'latest' ? 'latest' : 'oldest' );
            }
        };

        // Check if word filters are active
        const hasIncludeWords = filters?.includeWords?.trim()?.length > 0;
        const hasExcludeWords = filters?.excludeWords?.trim()?.length > 0;

        // Check if source filter is active (some sources deselected)
        const hasSourceFilter = selectedProviders.length < availableProviders.length;

        // Calculate active filters count (rating, char min, include words, exclude words, sources)
        const activeFiltersCount = ( minRating > 1 ? 1 : 0 ) + ( hasMinCharFilter ? 1 : 0 ) + ( hasIncludeWords ? 1 : 0 ) + ( hasExcludeWords ? 1 : 0 ) + ( hasSourceFilter ? 1 : 0 );

        /**
         * Clear all filters
         */
        const handleClearFilters = () => {
            updateNestedPopupSetting( 'filters', 'includedStarFilters', [] );
            updateNestedPopupSetting( 'filters', 'filterCharCountMin', 0 );
            updateNestedPopupSetting( 'filters', 'includeWords', '' );
            updateNestedPopupSetting( 'filters', 'excludeWords', '' );
            updateNestedPopupSetting( 'filters', 'providers', null ); // Reset to all sources
        };

        return (
            <>
            <div className="sb-customizer-sidebar-section-ctn sb-popup-section sb-fs">
                <PopupSectionHeader
                    title={ __( 'Filters', 'reviews-feed' ) }
                    onBack={ () => navigateToSection( 'sb-popup-settings-tab' ) }
                />

                <div className="sb-popup-section-controls sb-fs">

                {/* Filter Applied Notice */}
                <div className="sb-popup-filter-notice sb-fs">
                    <div className="sb-popup-filter-notice-header">
                        <span className="sb-popup-filter-notice-count">
                            { activeFiltersCount > 0
                                ? activeFiltersCount + ' ' + ( activeFiltersCount === 1 ? __( 'filter applied', 'reviews-feed' ) : __( 'filters applied', 'reviews-feed' ) )
                                : __( 'No filters applied', 'reviews-feed' )
                            }
                        </span>
                        { activeFiltersCount > 0 && (
                            <span
                                className="sb-popup-filter-notice-clear"
                                onClick={ handleClearFilters }
                            >
                                { __( 'Clear', 'reviews-feed' ) }
                            </span>
                        ) }
                    </div>
                    <div className="sb-popup-filter-notice-subtitle">
                        { previewReviews?.length || 0 } { __( 'out of', 'reviews-feed' ) } { unfilteredTotal || 0 } { __( 'reviews will be visible', 'reviews-feed' ) }
                    </div>
                </div>

                {/* Rating and Length */}
                <div className="sb-popup-control-group sb-fs">
                        <div className="sb-group-heading sb-text-small sb-bold sb-fs">{ __( 'Rating and Length', 'reviews-feed' ) }</div>
                        <div className="sb-popup-rating-filter sb-fs">
                            <div className="sb-popup-rating-row">
                                <div className="sb-popup-rating-stars">
                                    { [ 1, 2, 3, 4, 5 ].map( star => (
                                        <span
                                            key={ star }
                                            className={ `sb-popup-star ${ star <= minRating ? 'sb-popup-star--active' : '' }` }
                                            onClick={ () => handleRatingChange( star ) }
                                        >
                                            { SbUtils.printIcon( 'popup-star', '', star ) }
                                        </span>
                                    ) ) }
                                </div>
                                <span className="sb-popup-rating-label">
                                    { minRating === 1 ? __( 'Show all reviews', 'reviews-feed' ) : minRating + '+ ' + __( 'star reviews', 'reviews-feed' ) }
                                </span>
                            </div>
                            <div className="sb-popup-rating-slider" style={ { '--rating-percent': `${ ( ( minRating - 1 ) / 4 ) * 100 }%` } }>
                                <input
                                    type="range"
                                    min="1"
                                    max="5"
                                    value={ minRating }
                                    onChange={ ( e ) => handleRatingChange( parseInt( e.target.value, 10 ) ) }
                                />
                                <div className="sb-popup-rating-dots">
                                    { [ 1, 2, 3, 4, 5 ].map( dot => (
                                        <span
                                            key={ dot }
                                            className={ `sb-popup-rating-dot ${ dot <= minRating ? 'sb-popup-rating-dot--active' : '' }` }
                                        />
                                    ) ) }
                                </div>
                            </div>
                        </div>
                        <div className="sb-popup-checkbox-row sb-fs">
                            <label className="sb-popup-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={ hasMinCharFilter }
                                    onChange={ ( e ) => handleMinCharToggle( e.target.checked ) }
                                />
                                <span>{ __( 'Only show reviews with 50+ characters', 'reviews-feed' ) }</span>
                            </label>
                        </div>
                    </div>
                </div>

                {/* By Source */}
                <div className="sb-popup-static-section sb-popup-static-section--bordered sb-fs">
                <div className="sb-group-heading sb-text-small sb-bold sb-fs">{ __( 'By Source', 'reviews-feed' ) }</div>
                { availableProviders.length === 0 ? (
                    <div className="sb-popup-empty-hint sb-text-tiny sb-dark2-text">
                        { __( 'No sources configured. Add sources in Settings > Sources first.', 'reviews-feed' ) }
                    </div>
                ) : (
                    <div className="sb-popup-source-badges sb-fs">
                        { availableProviders.map( provider => {
                            const isSelected = selectedProviders.includes( provider );
                            const isLastSelected = isSelected && selectedProviders.length === 1;
                            return (
                                <button
                                    key={ provider }
                                    type="button"
                                    className={ `sb-popup-source-badge ${ isSelected ? 'sb-popup-source-badge--active' : '' } ${ isLastSelected ? 'sb-popup-source-badge--disabled' : '' }` }
                                    onClick={ () => handleProviderToggle( provider ) }
                                    style={ isLastSelected ? { cursor: 'not-allowed', opacity: 0.7 } : {} }
                                    title={ isLastSelected ? __( 'At least one source must be selected', 'reviews-feed' ) : '' }
                                >
                                    { isSelected && SbUtils.printIcon( 'success', 'sb-popup-badge-check', false, 12 ) }
                                    <span>{ provider.charAt( 0 ).toUpperCase() + provider.slice( 1 ) }</span>
                                </button>
                            );
                        } ) }
                    </div>
                ) }
            </div>

            {/* By Date (Sort) */}
            <div className="sb-popup-static-section sb-popup-static-section--bordered sb-fs">
                <div className="sb-group-heading sb-text-small sb-bold sb-fs">{ __( 'Sort by Date', 'reviews-feed' ) }</div>
                <div className="sb-theme-dropdown sb-toggleset-ctn">
                    { [
                        { value: 'latest', label: __( 'Newest first', 'reviews-feed' ) },
                        { value: 'oldest', label: __( 'Oldest first', 'reviews-feed' ) }
                    ].map( option => (
                        <div
                            key={ option.value }
                            className="sb-toggleset-elem sb-fs"
                            data-active={ sortByDateEnabled && sortByDate === option.value }
                            onClick={ () => handleSortChange( option.value ) }
                        >
                            <div className="sb-toggleset-elem-deco sb-tr-2"></div>
                            <div className="sb-toggleset-elem-label">
                                <span className="sb-fs">{ option.label }</span>
                            </div>
                        </div>
                    ) ) }
                </div>
            </div>

            {/* By Words */}
            <div className="sb-popup-static-section sb-popup-static-section--bordered sb-fs">
                <div className="sb-group-heading sb-text-small sb-bold sb-fs">{ __( 'By Words', 'reviews-feed' ) }</div>
                <div className="sb-control-input-wrapper sb-fs">
                    <label className="sb-text-small sb-dark2-text sb-fs">{ __( 'Only show reviews containing', 'reviews-feed' ) }</label>
                    <div className="sb-input-ctn sb-input-medium sb-fs">
                        <div className="sb-input-insider">
                            <input
                                type="text"
                                placeholder={ __( 'Comma-separated words...', 'reviews-feed' ) }
                                value={ filters?.includeWords || '' }
                                onChange={ ( e ) => updateNestedPopupSetting( 'filters', 'includeWords', e.target.value ) }
                            />
                        </div>
                    </div>
                </div>
                <div className="sb-control-input-wrapper sb-fs">
                    <label className="sb-text-small sb-dark2-text sb-fs">{ __( 'Hide reviews containing', 'reviews-feed' ) }</label>
                    <div className="sb-input-ctn sb-input-medium sb-fs">
                        <div className="sb-input-insider">
                            <input
                                type="text"
                                placeholder={ __( 'Comma-separated words...', 'reviews-feed' ) }
                                value={ filters?.excludeWords || '' }
                                onChange={ ( e ) => updateNestedPopupSetting( 'filters', 'excludeWords', e.target.value ) }
                            />
                        </div>
                    </div>
                </div>
            </div>
            </div>
            </>
        );
    };

    /**
     * Render visibility section
     * Page targeting settings with clean structure:
     * visibility.excluded.pages/categories/custom_post_types (arrays of IDs/slugs)
     */
    const renderVisibilitySection = () => {
        const visibility = settings?.visibility || {};
        const displayOn = visibility?.display_on || 'all';

        // Get excluded/specific arrays based on display mode
        const targetGroup = displayOn === 'all' ? 'excluded' : 'specific';
        const selectedPages = visibility?.[ targetGroup ]?.pages || [];
        const selectedCategories = visibility?.[ targetGroup ]?.categories || [];
        const selectedPostTypes = visibility?.[ targetGroup ]?.custom_post_types || [];

        // Get available pages, categories, and post types from sbCustomizer
        // No fallback demo data - controls will be disabled when data is not available
        const availablePages = sbCustomizer?.wordpressPageLists || [];
        const availableCategories = sbCustomizer?.wordpressCategories || [];
        const availablePostTypes = sbCustomizer?.wordpressPostTypes || [];

        /**
         * Helper: Get ID from item (handles both old format: just ID, and new format: {id, ...})
         */
        const getItemId = ( item ) => {
            if ( typeof item === 'object' && item !== null ) {
                return item.id;
            }
            return item; // Old format: item is the ID itself
        };

        /**
         * Helper: Get slug from post type item (handles both formats)
         */
        const getPostTypeSlug = ( item ) => {
            if ( typeof item === 'object' && item !== null ) {
                return item.name;
            }
            return item; // Old format: item is the slug itself
        };

        /**
         * Helper: Check if page/category is selected (handles both formats)
         */
        const isItemSelected = ( selectedItems, itemId ) => {
            return selectedItems.some( item => getItemId( item ) === itemId );
        };

        /**
         * Helper: Check if post type is selected (handles both formats)
         */
        const isPostTypeSelected = ( selectedItems, slug ) => {
            return selectedItems.some( item => getPostTypeSlug( item ) === slug );
        };

        /**
         * Helper: Get selected IDs for VisibilitySelector (handles both formats)
         */
        const getSelectedIds = ( selectedItems, getKey ) => {
            return selectedItems.map( item => getKey( item ) );
        };

        /**
         * Update visibility setting with proper nested structure
         */
        const updateVisibilitySetting = ( field, value ) => {
            const newVisibility = {
                ...visibility,
                [ targetGroup ]: {
                    ...visibility?.[ targetGroup ],
                    [ field ]: value
                }
            };
            updatePopupSetting( 'visibility', newVisibility );
        };

        /**
         * Handle page selection - stores full object with title and url
         */
        const handlePageSelect = ( item ) => {
            const itemId = item.id;
            if ( isItemSelected( selectedPages, itemId ) ) {
                // Remove: filter out items with matching ID
                updateVisibilitySetting( 'pages', selectedPages.filter( p => getItemId( p ) !== itemId ) );
            } else {
                // Add: store full object with id, title, url
                const pageData = {
                    id: item.id,
                    title: item.title,
                    url: item.url
                };
                updateVisibilitySetting( 'pages', [ ...selectedPages, pageData ] );
            }
        };

        /**
         * Handle page removal
         */
        const handlePageRemove = ( itemId ) => {
            updateVisibilitySetting( 'pages', selectedPages.filter( p => getItemId( p ) !== itemId ) );
        };

        /**
         * Handle category selection - stores full object with name and url
         */
        const handleCategorySelect = ( item ) => {
            const itemId = item.id;
            if ( isItemSelected( selectedCategories, itemId ) ) {
                updateVisibilitySetting( 'categories', selectedCategories.filter( c => getItemId( c ) !== itemId ) );
            } else {
                // Add: store full object with id, name, url
                const categoryData = {
                    id: item.id,
                    name: item.name,
                    url: item.url
                };
                updateVisibilitySetting( 'categories', [ ...selectedCategories, categoryData ] );
            }
        };

        /**
         * Handle category removal
         */
        const handleCategoryRemove = ( itemId ) => {
            updateVisibilitySetting( 'categories', selectedCategories.filter( c => getItemId( c ) !== itemId ) );
        };

        /**
         * Handle post type selection - stores full object with name and label
         */
        const handlePostTypeSelect = ( item ) => {
            const slug = item.name;
            if ( isPostTypeSelected( selectedPostTypes, slug ) ) {
                updateVisibilitySetting( 'custom_post_types', selectedPostTypes.filter( pt => getPostTypeSlug( pt ) !== slug ) );
            } else {
                // Add: store full object with name (slug) and label
                const postTypeData = {
                    name: item.name,
                    label: item.label
                };
                updateVisibilitySetting( 'custom_post_types', [ ...selectedPostTypes, postTypeData ] );
            }
        };

        /**
         * Handle post type removal
         */
        const handlePostTypeRemove = ( slug ) => {
            updateVisibilitySetting( 'custom_post_types', selectedPostTypes.filter( pt => getPostTypeSlug( pt ) !== slug ) );
        };

        return (
            <div className="sb-customizer-sidebar-section-ctn sb-popup-section sb-fs">
                <PopupSectionHeader
                    title={ __( 'Visibility', 'reviews-feed' ) }
                    onBack={ () => navigateToSection( 'sb-popup-settings-tab' ) }
                />

                <div className="sb-popup-section-controls sb-fs">
                    {/* Display On */}
                    <div className="sb-popup-control-group sb-fs">
                        <div className="sb-group-heading sb-text-small sb-bold sb-fs">{ __( 'Display on', 'reviews-feed' ) }</div>
                        <div className="sb-popup-type-options">
                            <label
                                className={ `sb-popup-type-card ${ displayOn === 'all' ? 'sb-active' : '' }` }
                                onClick={ () => updateNestedPopupSetting( 'visibility', 'display_on', 'all' ) }
                            >
                                <span className="sb-popup-type-radio"></span>
                                <span className="sb-popup-type-content">
                                    <span className="sb-popup-type-title">{ __( 'All pages', 'reviews-feed' ) }</span>
                                    <span className="sb-popup-type-desc">{ __( 'Display review alert on every page of your site', 'reviews-feed' ) }</span>
                                </span>
                            </label>
                            <label
                                className={ `sb-popup-type-card ${ displayOn === 'specific' ? 'sb-active' : '' }` }
                                onClick={ () => updateNestedPopupSetting( 'visibility', 'display_on', 'specific' ) }
                            >
                                <span className="sb-popup-type-radio"></span>
                                <span className="sb-popup-type-content">
                                    <span className="sb-popup-type-title">{ __( 'Specific pages', 'reviews-feed' ) }</span>
                                    <span className="sb-popup-type-desc">{ __( 'Choose which pages to display the review alert', 'reviews-feed' ) }</span>
                                </span>
                            </label>
                        </div>
                    </div>

                    {/* Excluded/Specific Pages Section */}
                    <div className="sb-popup-excluded-pages sb-fs">
                        <div className="sb-popup-excluded-header sb-fs">
                            { SbUtils.printIcon( displayOn === 'all' ? 'minus-circle' : 'plus-circle', 'sb-popup-excluded-icon', false, 16 ) }
                            <span className="sb-popup-excluded-title">
                                { displayOn === 'all' ? __( 'Excluded pages', 'reviews-feed' ) : __( 'Specific pages', 'reviews-feed' ) }
                            </span>
                        </div>
                        <p className="sb-popup-excluded-desc">
                            { displayOn === 'all'
                                ? __( 'Review Alert will not be visible on these pages', 'reviews-feed' )
                                : __( 'Review Alert will only be visible on these pages', 'reviews-feed' )
                            }
                        </p>

                        {/* Page Selection */}
                        <div className="sb-popup-page-buttons sb-fs">
                            {/* Pages */}
                            <VisibilitySelector
                                label={ __( 'Pages', 'reviews-feed' ) }
                                type="page"
                                icon="document"
                                items={ availablePages }
                                selectedIds={ getSelectedIds( selectedPages, getItemId ) }
                                onSelect={ handlePageSelect }
                                onRemove={ handlePageRemove }
                                searchPlaceholder={ __( 'Search pages...', 'reviews-feed' ) }
                                itemConfig={ {
                                    getKey: ( item ) => item.id,
                                    getLabel: ( item ) => item.title,
                                    getUrl: ( item ) => item.url
                                } }
                                disabled={ availablePages.length === 0 }
                            />

                            {/* Categories */}
                            <VisibilitySelector
                                label={ __( 'Categories', 'reviews-feed' ) }
                                type="category"
                                icon="folder"
                                items={ availableCategories }
                                selectedIds={ getSelectedIds( selectedCategories, getItemId ) }
                                onSelect={ handleCategorySelect }
                                onRemove={ handleCategoryRemove }
                                searchPlaceholder={ __( 'Search categories...', 'reviews-feed' ) }
                                itemConfig={ {
                                    getKey: ( item ) => item.id,
                                    getLabel: ( item ) => item.name,
                                    getUrl: ( item ) => item.url
                                } }
                                disabled={ availableCategories.length === 0 }
                            />

                            {/* Custom Post Types */}
                            <VisibilitySelector
                                label={ __( 'Custom Post Types', 'reviews-feed' ) }
                                type="custom_post_type"
                                icon="document"
                                items={ availablePostTypes }
                                selectedIds={ getSelectedIds( selectedPostTypes, getPostTypeSlug ) }
                                onSelect={ handlePostTypeSelect }
                                onRemove={ handlePostTypeRemove }
                                searchPlaceholder={ __( 'Search post types...', 'reviews-feed' ) }
                                itemConfig={ {
                                    getKey: ( item ) => item.name,
                                    getLabel: ( item ) => item.label,
                                    getUrl: ( item ) => item.name
                                } }
                                disabled={ availablePostTypes.length === 0 }
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    /**
     * Render sources section
     * Allows selecting which review sources to display in the popup
     */
    const renderSourcesSection = () => {
        // Get popup sources from settings or empty array
        // Uses database ID instead of account_id to avoid URL encoding issues (PR #418)
        // Backward compatibility: also check account_id for existing popups with old format
        const popupSources = settings?.sources || [];

        // Get the source objects that match the selected IDs
        // Check both id (new format) and account_id (old format) for backward compatibility
        // Convert to strings for comparison to handle type mismatches (number vs string)
        const popupSourcesStr = popupSources.map( s => String( s ) );
        const feedSourcesList = sources?.sourcesList?.filter(
            source => popupSourcesStr.includes( String( source.id ) ) || popupSourcesStr.includes( String( source.account_id ) )
        ) || [];

        // Handle sources change
        const handleSourcesChange = ( newSources ) => {
            updatePopupSetting( 'sources', newSources );
        };

        return (
            <div className="sb-customizer-sidebar-section-ctn sb-popup-section sb-fs">
                <PopupSectionHeader
                    title={ __( 'Sources', 'reviews-feed' ) }
                    onBack={ () => navigateToSection( 'sb-popup-settings-tab' ) }
                />

                <div className="sb-popup-section-controls sb-fs">
                    {/* Empty State */}
                    { ( !popupSources || popupSources.length === 0 ) && (
                        <div className="sb-popup-empty-sources sb-fs">
                            <span className="sb-popup-empty-sources-title">{ __( 'No sources added yet.', 'reviews-feed' ) }</span>
                            <span className="sb-popup-empty-sources-text">{ __( 'Add a source to display reviews.', 'reviews-feed' ) }</span>
                        </div>
                    ) }

                    <div
                        className="sb-fs"
                        data-type="feedsources"
                        data-layout="block"
                        data-id="sources"
                    >
                        <div className="sb-control-content sb-fs">
                            <div className="sb-control-elem-output">
                                <FeedSources
                                    customClass="sb-feedsources-control"
                                    sources={ sources }
                                    feedSourcesList={ feedSourcesList }
                                    value={ popupSources }
                                    fbModal={ fbModal }
                                    fbManualModal={ fbManualModal }
                                    onChange={ handleSourcesChange }
                                    useDbId={ true }
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    /**
     * Render theme section controls
     */
    const renderThemeSection = () => (
        <div className="sb-customizer-sidebar-section-ctn sb-fs">
            <div className="sb-customizer-sidebar-header sb-fs">
                <div className="sb-customizer-sidebar-breadcrumbs sb-fs">
                    <span
                        className="sb-customizer-breadcrumbs-elm"
                        onClick={ () => navigateToSection( 'sb-popup-tab' ) }
                    >
                        { __( 'Customize', 'reviews-feed' ) }
                    </span>
                    <span className="sb-customizer-breadcrumbs-elm">
                        { __( 'Theme', 'reviews-feed' ) }
                    </span>
                </div>
            </div>
            <div className="sb-customizer-sidebar-section-controls sb-fs">
                <ThemeSelector
                    settings={ settings }
                    onChange={ handleThemeChange }
                    isPro={ isPro }
                    upsellModal={ upsellModal }
                />
            </div>
        </div>
    );

    /**
     * Render accent color section controls
     */
    const renderColorSection = () => (
        <div className="sb-customizer-sidebar-section-ctn sb-fs">
            <div className="sb-customizer-sidebar-header sb-fs">
                <div className="sb-customizer-sidebar-breadcrumbs sb-fs">
                    <span
                        className="sb-customizer-breadcrumbs-elm"
                        onClick={ () => navigateToSection( 'sb-popup-tab' ) }
                    >
                        { __( 'Customize', 'reviews-feed' ) }
                    </span>
                    <span className="sb-customizer-breadcrumbs-elm">
                        { __( 'Accent Color', 'reviews-feed' ) }
                    </span>
                </div>
            </div>
            <div className="sb-customizer-sidebar-section-controls sb-fs">
                <div className="sb-control-elem-ctn sb-fs">
                    <div className="sb-control-elem-label sb-fs">
                        <span className="sb-control-label sb-bold sb-text-tiny">
                            { __( 'Accent Color', 'reviews-feed' ) }
                        </span>
                    </div>
                    <div className="sb-control-elem-input sb-fs">
                        <input
                            type="color"
                            className="sb-color-input"
                            value={ settings?.accent_color || '#175CE3' }
                            onChange={ ( e ) => updatePopupSetting( 'accent_color', e.target.value ) }
                        />
                        <span className="sb-color-value sb-text-small">
                            { settings?.accent_color || '#175CE3' }
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );

    /**
     * Render Review Feed section (expanded popup settings)
     * Matches Vercel demo design: Back button, Header, Review Cards, Branding
     */
    const renderReviewFeedSection = () => {
        const reviewFeed = settings?.review_feed || {};

        return (
            <div className="sb-customizer-sidebar-section-ctn sb-popup-section sb-fs">
                <PopupSectionHeader
                    title={ __( 'Review Feed', 'reviews-feed' ) }
                    onBack={ () => navigateToSection( 'sb-popup-tab' ) }
                />

                <div className="sb-popup-section-controls sb-fs">
                    {/* Header Section */}
                    <div className="sb-popup-control-group sb-fs">
                        <div className="sb-group-heading sb-text-small sb-bold sb-fs">{ __( 'Header', 'reviews-feed' ) }</div>
                        <div className="sb-popup-elements-list sb-popup-header-list">
                            {/* Heading Item with Edit dropdown */}
                            <div className={ `sb-popup-element-item sb-popup-header-item ${ reviewFeed?.show_heading !== false ? 'sb-visible' : 'sb-hidden' }` }>
                                <button
                                    className="sb-popup-header-toggle"
                                    onClick={ () => updateNestedPopupSetting( 'review_feed', 'show_heading', reviewFeed?.show_heading === false ) }
                                >
                                    { SbUtils.printIcon( reviewFeed?.show_heading !== false ? 'eye' : 'eyeslash', 'sb-popup-element-icon' ) }
                                    <span className="sb-popup-element-label">{ __( 'Heading', 'reviews-feed' ) }</span>
                                </button>
                                <button
                                    className="sb-popup-header-edit"
                                    onClick={ () => toggleSection( 'headingEdit' ) }
                                >
                                    <span>{ __( 'Edit', 'reviews-feed' ) }</span>
                                    { SbUtils.printIcon( expandedSections.headingEdit ? 'chevron-top' : 'chevron-bottom' ) }
                                </button>
                            </div>
                            <div className={ `sb-popup-header-edit-panel sb-edit-panel-animate ${ expandedSections.headingEdit ? 'sb-edit-panel-open' : '' }` }>
                                <div className="sb-popup-edit-panel-inner">
                                    <label className="sb-popup-edit-label">
                                        <span>{ __( 'Heading text', 'reviews-feed' ) }</span>
                                        <input
                                            type="text"
                                            className="sb-popup-edit-input"
                                            placeholder={ __( 'Enter heading text...', 'reviews-feed' ) }
                                            value={ reviewFeed?.heading_text || '' }
                                            onChange={ ( e ) => updateNestedPopupSetting( 'review_feed', 'heading_text', e.target.value ) }
                                        />
                                    </label>
                                </div>
                            </div>

                            {/* Button Item with Edit dropdown */}
                            <div className={ `sb-popup-element-item sb-popup-header-item ${ reviewFeed?.show_button !== false ? 'sb-visible' : 'sb-hidden' }` }>
                                <button
                                    className="sb-popup-header-toggle"
                                    onClick={ () => updateNestedPopupSetting( 'review_feed', 'show_button', reviewFeed?.show_button === false ) }
                                >
                                    { SbUtils.printIcon( reviewFeed?.show_button !== false ? 'eye' : 'eyeslash', 'sb-popup-element-icon' ) }
                                    <span className="sb-popup-element-label">{ __( 'Button', 'reviews-feed' ) }</span>
                                </button>
                                <button
                                    className="sb-popup-header-edit"
                                    onClick={ () => toggleSection( 'buttonEdit' ) }
                                >
                                    <span>{ __( 'Edit', 'reviews-feed' ) }</span>
                                    { SbUtils.printIcon( expandedSections.buttonEdit ? 'chevron-top' : 'chevron-bottom' ) }
                                </button>
                            </div>
                            <div className={ `sb-popup-header-edit-panel sb-edit-panel-animate ${ expandedSections.buttonEdit ? 'sb-edit-panel-open' : '' }` }>
                                <div className="sb-popup-edit-panel-inner">
                                    <label className="sb-popup-edit-label">
                                        <span>{ __( 'Button text', 'reviews-feed' ) }</span>
                                        <input
                                            type="text"
                                            className="sb-popup-edit-input"
                                            placeholder={ __( 'Enter button text...', 'reviews-feed' ) }
                                            value={ reviewFeed?.button_text || '' }
                                            onChange={ ( e ) => updateNestedPopupSetting( 'review_feed', 'button_text', e.target.value ) }
                                        />
                                    </label>
                                    <label className="sb-popup-edit-label">
                                        <span>{ __( 'Button URL', 'reviews-feed' ) }</span>
                                        <input
                                            type="text"
                                            className="sb-popup-edit-input"
                                            placeholder="https://example.com"
                                            value={ reviewFeed?.button_url || '' }
                                            onChange={ ( e ) => updateNestedPopupSetting( 'review_feed', 'button_url', e.target.value ) }
                                        />
                                    </label>
                                    {/* Icon Picker */}
                                    <div className="sb-popup-icon-picker">
                                        <span className="sb-popup-icon-picker-label">{ __( 'Icon (optional)', 'reviews-feed' ) }</span>
                                        <div className="sb-popup-icon-picker-grid">
                                            {/* No icon option */}
                                            <button
                                                type="button"
                                                className={ `sb-popup-icon-btn ${ !reviewFeed?.button_icon ? 'sb-popup-icon-btn-selected' : 'sb-popup-icon-btn-unselected' }` }
                                                onClick={ () => updateNestedPopupSetting( 'review_feed', 'button_icon', null ) }
                                                title={ __( 'No icon', 'reviews-feed' ) }
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="sb-popup-icon-size">
                                                    <path d="M5.28 4.22a.75.75 0 0 0-1.06 1.06L6.94 8l-2.72 2.72a.75.75 0 1 0 1.06 1.06L8 9.06l2.72 2.72a.75.75 0 1 0 1.06-1.06L9.06 8l2.72-2.72a.75.75 0 0 0-1.06-1.06L8 6.94 5.28 4.22Z" />
                                                </svg>
                                            </button>
                                            {/* Icon options */}
                                            { BUTTON_ICONS.map( ( icon ) => (
                                                <button
                                                    key={ icon.id }
                                                    type="button"
                                                    className={ `sb-popup-icon-btn ${ reviewFeed?.button_icon === icon.id ? 'sb-popup-icon-btn-selected' : 'sb-popup-icon-btn-unselected-colored' }` }
                                                    onClick={ () => updateNestedPopupSetting( 'review_feed', 'button_icon', reviewFeed?.button_icon === icon.id ? null : icon.id ) }
                                                    title={ icon.label }
                                                >
                                                    <span
                                                        className="sb-popup-icon-size"
                                                        dangerouslySetInnerHTML={{ __html: icon.svg }}
                                                    />
                                                </button>
                                            ) ) }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Review Cards Section */}
                    <div className="sb-popup-control-group sb-fs">
                        <div className="sb-group-heading sb-text-small sb-bold sb-fs">{ __( 'Review Cards', 'reviews-feed' ) }</div>
                        <div className="sb-popup-elements-list">
                            { ( () => {
                                // Count visible elements (excluding branding - it can't be the last visible)
                                const reviewCardVisibleCount = REVIEW_CARD_ELEMENTS.filter(
                                    el => reviewFeed?.[ el.key ] !== false
                                ).length;

                                return REVIEW_CARD_ELEMENTS.map( ( element ) => {
                                    const isVisible = reviewFeed?.[ element.key ] !== false;
                                    const isLastVisible = isVisible && reviewCardVisibleCount === 1;
                                    const isDisabled = isLastVisible;

                                    return (
                                        <button
                                            key={ element.key }
                                            className={ `sb-popup-element-item ${ isVisible ? 'sb-visible' : 'sb-hidden' } ${ isDisabled ? 'sb-disabled' : '' }` }
                                            disabled={ isDisabled }
                                            title={ isLastVisible ? __( 'At least one element must be visible', 'reviews-feed' ) : '' }
                                            onClick={ () => {
                                                if ( isDisabled ) return;
                                                updateNestedPopupSetting( 'review_feed', element.key, !isVisible );
                                            } }
                                        >
                                            { SbUtils.printIcon( isVisible ? 'eye' : 'eyeslash', 'sb-popup-element-icon' ) }
                                            <span className="sb-popup-element-label">{ __( element.label, 'reviews-feed' ) }</span>
                                        </button>
                                    );
                                } );
                            } )() }
                        </div>
                    </div>

                    {/* Branding Section */}
                    <div className="sb-popup-control-group sb-fs" style={{ marginBottom: '20px' }}>
                        <div className="sb-group-heading sb-text-small sb-bold sb-fs">{ __( 'Branding', 'reviews-feed' ) }</div>
                        <div className="sb-popup-elements-list">
                            <button
                                className={ `sb-popup-element-item ${ reviewFeed?.show_powered_by !== false ? 'sb-visible' : 'sb-hidden' }` }
                                onClick={ () => {
                                    if ( !sbCustomizer?.isProPlus ) {
                                        SbUtils.openUpsellModal( 'reviewAlertBranding', upsellModal );
                                        return;
                                    }
                                    updateNestedPopupSetting( 'review_feed', 'show_powered_by', reviewFeed?.show_powered_by === false );
                                } }
                            >
                                { SbUtils.printIcon( reviewFeed?.show_powered_by !== false ? 'eye' : 'eyeslash', 'sb-popup-element-icon' ) }
                                <span className="sb-popup-element-label">
                                    { __( 'Powered By', 'reviews-feed' ) }
                                    { !sbCustomizer?.isProPlus && SbUtils.printIcon( 'rocket-premium', 'sb-popup-pro-icon' ) }
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    /**
     * Render position section controls
     */
    const renderPositionSection = () => (
        <div className="sb-customizer-sidebar-section-ctn sb-fs">
            <div className="sb-customizer-sidebar-header sb-fs">
                <div className="sb-customizer-sidebar-breadcrumbs sb-fs">
                    <span
                        className="sb-customizer-breadcrumbs-elm"
                        onClick={ () => navigateToSection( 'sb-popup-tab' ) }
                    >
                        { __( 'Customize', 'reviews-feed' ) }
                    </span>
                    <span className="sb-customizer-breadcrumbs-elm">
                        { __( 'Positioning', 'reviews-feed' ) }
                    </span>
                </div>
            </div>
            <div className="sb-customizer-sidebar-section-controls sb-fs">
                <div className="sb-control-elem-ctn sb-fs">
                    <div className="sb-control-elem-label sb-fs">
                        <span className="sb-control-label sb-bold sb-text-tiny">
                            { __( 'Position', 'reviews-feed' ) }
                        </span>
                    </div>
                    <div className="sb-control-elem-input sb-fs">
                        <select
                            className="sb-control-select"
                            value={ settings?.position || 'bottom-right' }
                            onChange={ ( e ) => updatePopupSetting( 'position', e.target.value ) }
                        >
                            <option value="bottom-right">{ __( 'Right', 'reviews-feed' ) }</option>
                            <option value="bottom-left">{ __( 'Left', 'reviews-feed' ) }</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );

    /**
     * Render settings tab (Sources, Behavior & Location)
     */
    const renderSettingsSection = () => (
        <>
            {/* Sources Section */}
            <div
                className="sb-customizer-sidebar-sec-el sb-customizer-sidebar-sec-el-nav sb-fs"
                onClick={ () => navigateToSection( 'sb-popup-sources-tab' ) }
            >
                { SbUtils.printIcon( 'sourcesadd', 'sb-customizer-sidebar-sec-el-icon' ) }
                <span className="sb-small-p sb-bold sb-dark-text">
                    { __( 'Sources', 'reviews-feed' ) }
                </span>
                { SbUtils.printIcon( 'chevron-right', 'sb-customizer-sec-el-chevron' ) }
            </div>

            {/* Filter Section */}
            <div
                className="sb-customizer-sidebar-sec-el sb-customizer-sidebar-sec-el-nav sb-fs"
                onClick={ () => navigateToSection( 'sb-popup-filter-tab' ) }
            >
                { SbUtils.printIcon( 'filter', 'sb-customizer-sidebar-sec-el-icon' ) }
                <span className="sb-small-p sb-bold sb-dark-text">
                    { __( 'Filter', 'reviews-feed' ) }
                </span>
                { SbUtils.printIcon( 'chevron-right', 'sb-customizer-sec-el-chevron' ) }
            </div>

            {/* Visibility Section */}
            <div
                className="sb-customizer-sidebar-sec-el sb-customizer-sidebar-sec-el-nav sb-fs"
                onClick={ () => navigateToSection( 'sb-popup-visibility-tab' ) }
            >
                { SbUtils.printIcon( 'eye', 'sb-customizer-sidebar-sec-el-icon' ) }
                <span className="sb-small-p sb-bold sb-dark-text">
                    { __( 'Visibility', 'reviews-feed' ) }
                </span>
                { SbUtils.printIcon( 'chevron-right', 'sb-customizer-sec-el-chevron' ) }
            </div>
        </>
    );

    /**
     * Get section content based on current tab
     */
    const getSectionContent = () => {
        switch ( sidebarSection.currentSidebarSection ) {
            case 'sb-popup-theme-section':
                return renderThemeSection();
            case 'sb-popup-color-section':
                return renderColorSection();
            case 'sb-popup-position-section':
                return renderPositionSection();
            case 'sb-popup-content-tab':
                return renderContentSection();
            case 'sb-popup-review-feed-section':
                return renderReviewFeedSection();
            case 'sb-popup-sources-tab':
                return renderSourcesSection();
            case 'sb-popup-filter-tab':
                return renderFiltersSection();
            case 'sb-popup-visibility-tab':
                return renderVisibilitySection();
            case 'sb-popup-settings-tab':
                return renderSettingsSection();
            case 'sb-popup-tab':
            default:
                return renderMainSection();
        }
    };

    /**
     * Render sidebar tabs navigation
     */
    const renderSidebarTabs = () => {
        const tabs = [
            { id: 'sb-popup-tab', label: __( 'Customize', 'reviews-feed' ) },
            { id: 'sb-popup-settings-tab', label: __( 'Settings', 'reviews-feed' ) }
        ];

        return tabs.map( ( tab ) => (
            <div
                key={ tab.id }
                className="sb-customizer-sidebar-tab"
                data-active={ sidebarSection.currentSidebarSection === tab.id || ( tab.id === 'sb-popup-tab' && [ 'sb-popup-tab', 'sb-popup-content-tab' ].includes( sidebarSection.currentSidebarSection ) ) || ( tab.id === 'sb-popup-settings-tab' && [ 'sb-popup-settings-tab', 'sb-popup-sources-tab', 'sb-popup-filter-tab', 'sb-popup-visibility-tab' ].includes( sidebarSection.currentSidebarSection ) ) }
                onClick={ () => navigateToSection( tab.id ) }
            >
                <span className="sb-standard-p sb-bold">{ tab.label }</span>
            </div>
        ) );
    };

    // Build animation class for section content
    const getAnimationClass = () => {
        if ( !slideDirection ) return '';
        return slideDirection === 'forward' ? 'sb-slide-from-right' : 'sb-slide-from-left';
    };

    return (
        <section className="sb-customizer-sidebar">
            <div className="sb-customizer-sidebar-content sb-fs">
                { SECTION_DEPTH[ sidebarSection.currentSidebarSection ] === 0 && (
                    <div className="sb-customizer-sidebar-tabs sb-fs">
                        { renderSidebarTabs() }
                    </div>
                ) }

                <div className="sb-customizer-sidebar-sections sb-fs">
                    <div
                        key={ sidebarSection.currentSidebarSection }
                        className={ `sb-sidebar-section-content ${ getAnimationClass() }` }
                    >
                        { getSectionContent() }
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ReviewAlertSidebar;
